/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || | 
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import { Badge, Box, Button, Icon, SimpleGrid, Spinner, Stack } from "@chakra-ui/react";
import React, { useContext, useMemo, useState } from "react";
import { useContent } from "hooks/useContent";
import { AuthContext } from "contexts/AuthContext";
import { DataContext } from "contexts/DataContext";
import { useBroadcast } from "hooks/useBroadcast";
import { MdClose } from "react-icons/md";
import { ActionDrawer } from "components/drawer";
import BroadcastModal from "views/modal/broadcast";
import { remove } from "lib/firebase";
import DataTableNew from "components/table/DataTableNew";
import { columnsBroadcasts } from "./columns/broadcasts";
import MultiSelectFilter from "components/filter/MultiSelectFilter";
import { broadcastLinks } from "lib/membership";

export default function BroadcastsPage() {
  const { user } = useContext(AuthContext)
  const [broadcasts, loading, error, refreshBroadcasts] = useBroadcast({ user })

  // Where each broadcast is used. The copies are needed too: a composite broadcast may list a
  // multiplier copy instead of the original. Groups and locations come from the shared lists.
  const { broadcasts: allBroadcasts, groups, locations } = useContext(DataContext)
  const [allBroadcastItems] = allBroadcasts
  const [groupItems, , , refreshGroups] = groups
  const [locationItems, , , refreshLocations] = locations

  // The same object while nothing changes, so the table keeps its search texts
  const links = useMemo(
    () => broadcastLinks({ broadcasts: allBroadcastItems, groups: groupItems, locations: locationItems }),
    [allBroadcastItems, groupItems, locationItems]
  )
  const tableContext = useMemo(() => ({ links }), [links])

  // Refresh reloads groups and locations too: the "Groups", "Locations" and "In composite" columns come from them
  const refresh = () => Promise.all([refreshBroadcasts(), refreshGroups(), refreshLocations()])

  // Chosen values of each filter; an empty list means "any"
  const [filter, setFilter] = useState({
    chyron: [],
    types: []
  })

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} flex="1" minH={0} display="flex" flexDirection="column">
      <DataTableNew
        columns={columnsBroadcasts}
        data={broadcasts.filter(br => {
          // if (br.broadcastList) return false
          if (filter.types.length > 0 && !filter.types.includes(br.broadcastList ? 'composite' : 'regular')) return false
          if (filter.chyron.length > 0 && !filter.chyron.includes(br.showChyron ? 'yes' : 'no')) return false
          return true
        })}
        totalCount={broadcasts.length}
        loading={loading}
        refresh={refresh}
        context={tableContext}
        resetPageKey={filter}
        defaultSort={[{ id: 'name', desc: false }]}
        filters={(
          <>
          <MultiSelectFilter label='Any broadcast' width='190px'
            options={[
              { value: 'regular', label: 'Regular broadcast' },
              { value: 'composite', label: 'Composite broadcast' },
            ]}
            value={filter.types}
            onChange={types => setFilter({ ...filter, types })} />
          <MultiSelectFilter label='Any chyron' width='170px'
            options={[
              { value: 'yes', label: 'With chyron' },
              { value: 'no', label: 'No chyron' },
            ]}
            value={filter.chyron}
            onChange={chyron => setFilter({ ...filter, chyron })} />
          </>
        )}
        actions={(
          <>
          <ActionDrawer
            title='Create broadcast'
            size={'xxl'}
            trigger={
              <Button colorScheme='brand' variant='solid' size='sm' borderRadius={30} px='8'>
                New broadcast
              </Button>
            }
            >
            <BroadcastModal onChange={refresh} />
          </ActionDrawer>
          </>
        )}
      />
    </Box>
  );
}
